import React, { useState } from 'react';
import {
  Card,
  Radio,
  Select,
  Button,
  Table,
  Space,
  Typography,
  Alert,
  Row,
  Col,
  Statistic,
  Progress,
  Tag,
  Tooltip,
} from 'antd';
import {
  DownloadOutlined,
  SettingOutlined,
  ScanOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

import { PredictionFormData, PredictionType } from '../../types/prediction';

const { Title, Text } = Typography;
const { Option } = Select;

interface StrategyAnalysisProps {
  predictionResult: any;
  patientData: PredictionFormData;
  predictionType: PredictionType;
}

interface TreatmentStrategy {
  id: string;
  protocol: string;
  fshDose: string;
  rfsh: string;
  lh: string;
  porAvoidance: number;
  horAvoidance: number;
  overallScore: number;
  recommendation: string;
}

const StrategyAnalysis: React.FC<StrategyAnalysisProps> = ({
  predictionResult,
  patientData,
  predictionType,
}) => {
  const [analysisMode, setAnalysisMode] = useState<'full' | 'specific'>('full');
  const [selectedProtocol, setSelectedProtocol] = useState<string>('');
  const [selectedFSHDose, setSelectedFSHDose] = useState<string>('');
  const [selectedRFSH, setSelectedRFSH] = useState<string>('');
  const [selectedLH, setSelectedLH] = useState<string>('');
  const [strategies, setStrategies] = useState<TreatmentStrategy[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const protocolOptions = [
    { value: 'long', label: 'Long Protocol' },
    { value: 'ultra-long', label: 'Ultra-long Protocol' },
    { value: 'short', label: 'Short Protocol' },
    { value: 'antagonist', label: 'Antagonist Protocol' },
    { value: 'mild-natural', label: 'Mild/Natural Protocol' },
    { value: 'ppos', label: 'PPOS Protocol' },
    { value: 'other', label: 'Other' },
  ];

  const fshDoseOptions = [
    { value: '≤100', label: '≤100 IU' },
    { value: '150', label: '150 IU' },
    { value: '200', label: '200 IU' },
    { value: '225', label: '225 IU' },
    { value: '≥300', label: '≥300 IU' },
    { value: 'none', label: 'None' },
  ];

  const yesNoOptions = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'none', label: 'None' },
  ];

  // Generate comprehensive strategy analysis — Full Scanning produces all valid combos
  const generateStrategies = (): TreatmentStrategy[] => {
    const results: TreatmentStrategy[] = [];

    const porRisk = predictionResult?.por_prediction?.poor_response_prob || 0;
    const horRisk = predictionResult?.hor_prediction?.high_response_prob || 0;
    const age = patientData.Age || 30;
    const amh = patientData.AMH || 2;
    const hasPCOS = !!patientData.PCOS;
    const hasPOI = !!patientData.POIorDOR;

    // Clinically meaningful combinations
    const protocols = protocolOptions;
    const doses = fshDoseOptions.filter(d => d.value !== 'none');
    const rfshOpts = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
    const lhOpts = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];

    let idx = 0;
    for (const proto of protocols) {
      for (const dose of doses) {
        for (const rfsh of rfshOpts) {
          for (const lh of lhOpts) {
            // Skip clinically implausible pairs
            if (proto.value === 'mild-natural' && (dose.value === '225' || dose.value === '≥300')) continue;
            if (proto.value === 'ppos' && rfsh.value === 'no' && lh.value === 'yes') continue;

            // Compute POR avoidance score
            let porScore = 0.5;
            // Higher dose → better POR avoidance
            const doseMap: Record<string, number> = { '≤100': -0.1, '150': 0, '200': 0.05, '225': 0.1, '≥300': 0.15 };
            porScore += doseMap[dose.value] || 0;
            // rFSH improves POR avoidance
            if (rfsh.value === 'yes') porScore += 0.08;
            // LH supplementation helps POR
            if (lh.value === 'yes') porScore += 0.05;
            // Protocol effects
            const protoMap: Record<string, number> = {
              'long': 0.12, 'ultra-long': 0.1, 'short': 0.08,
              'antagonist': 0.06, 'mild-natural': -0.05, 'ppos': 0.07, 'other': 0.03,
            };
            porScore += protoMap[proto.value] || 0;
            // Patient-specific adjustments
            if (hasPOI && dose.value === '≥300') porScore += 0.05;
            if (age > 38) porScore -= 0.05;
            if (amh < 1) porScore -= 0.08;
            porScore = Math.min(0.99, Math.max(0.3, porScore + 0.35));

            // Compute HOR avoidance score
            let horScore = 0.5;
            // Lower dose → better HOR avoidance
            const dosHorMap: Record<string, number> = { '≤100': 0.15, '150': 0.08, '200': 0, '225': -0.08, '≥300': -0.15 };
            horScore += dosHorMap[dose.value] || 0;
            // Antagonist protocol best for HOR avoidance
            const protoHorMap: Record<string, number> = {
              'long': -0.02, 'ultra-long': -0.05, 'short': 0,
              'antagonist': 0.12, 'mild-natural': 0.15, 'ppos': 0.1, 'other': 0.02,
            };
            horScore += protoHorMap[proto.value] || 0;
            if (hasPCOS) horScore -= 0.05;
            if (amh > 4) horScore -= 0.06;
            horScore = Math.min(0.99, Math.max(0.3, horScore + 0.35));

            // Overall = weighted average based on prediction type
            let overall: number;
            if (predictionType === 'POR') {
              overall = porScore * 0.8 + horScore * 0.2;
            } else if (predictionType === 'HOR') {
              overall = porScore * 0.2 + horScore * 0.8;
            } else {
              overall = porScore * 0.5 + horScore * 0.5;
            }

            // Generate recommendation tag
            let rec = 'Standard approach';
            if (overall >= 0.85) rec = 'Highly recommended';
            else if (overall >= 0.75) rec = 'Recommended';
            else if (overall >= 0.65) rec = 'Consider with caution';
            else rec = 'Low priority';

            idx++;
            results.push({
              id: String(idx),
              protocol: proto.label,
              fshDose: dose.label,
              rfsh: rfsh.label,
              lh: lh.label,
              porAvoidance: Math.round(porScore * 1000) / 1000,
              horAvoidance: Math.round(horScore * 1000) / 1000,
              overallScore: Math.round(overall * 1000) / 1000,
              recommendation: rec,
            });
          }
        }
      }
    }

    return results.sort((a, b) => b.overallScore - a.overallScore);
  };

  const handleAnalyze = async () => {
    setIsAnalyzing(true);

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    if (analysisMode === 'full') {
      // Generate comprehensive analysis
      const allStrategies = generateStrategies();
      setStrategies(allStrategies.slice(0, 5));
    } else {
      // Generate specific strategy analysis
      const specificStrategy: TreatmentStrategy = {
        id: 'specific',
        protocol: selectedProtocol,
        fshDose: selectedFSHDose,
        rfsh: selectedRFSH,
        lh: selectedLH,
        porAvoidance: Math.random() * 0.3 + 0.7, // Random between 0.7-1.0
        horAvoidance: Math.random() * 0.3 + 0.7,
        overallScore: Math.random() * 0.3 + 0.7,
        recommendation: 'Custom strategy analysis',
      };
      setStrategies([specificStrategy]);
    }

    setIsAnalyzing(false);
  };

  const handleDownloadStrategies = (type: 'por' | 'hor' | 'combined') => {
    const filename = `Top_5_Suggested_Ovarian_Stimulation_Strategies_${type.toUpperCase()}.csv`;

    // Create CSV content
    const headers = ['Protocol', 'FSH Dose', 'rFSH', 'LH', 'POR Avoidance', 'HOR Avoidance', 'Overall Score', 'Recommendation'];
    const csvContent = [
      headers.join(','),
      ...strategies.map(strategy => [
        strategy.protocol,
        strategy.fshDose,
        strategy.rfsh,
        strategy.lh,
        (strategy.porAvoidance * 100).toFixed(1) + '%',
        (strategy.horAvoidance * 100).toFixed(1) + '%',
        (strategy.overallScore * 100).toFixed(1) + '%',
        `"${strategy.recommendation}"`,
      ].join(','))
    ].join('\n');

    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const columns = [
    {
      title: 'Protocol',
      dataIndex: 'protocol',
      key: 'protocol',
      width: 150,
    },
    {
      title: 'FSH Dose',
      dataIndex: 'fshDose',
      key: 'fshDose',
      width: 100,
    },
    {
      title: 'rFSH',
      dataIndex: 'rfsh',
      key: 'rfsh',
      width: 80,
    },
    {
      title: 'LH',
      dataIndex: 'lh',
      key: 'lh',
      width: 80,
    },
    {
      title: 'POR Avoidance',
      dataIndex: 'porAvoidance',
      key: 'porAvoidance',
      width: 120,
      render: (value: number) => (
        <Space>
          <Progress
            type="circle"
            size={40}
            percent={value * 100}
            format={percent => `${percent?.toFixed(0)}%`}
            strokeColor={value > 0.8 ? '#52c41a' : value > 0.6 ? '#faad14' : '#ff4d4f'}
          />
        </Space>
      ),
    },
    {
      title: 'HOR Avoidance',
      dataIndex: 'horAvoidance',
      key: 'horAvoidance',
      width: 120,
      render: (value: number) => (
        <Space>
          <Progress
            type="circle"
            size={40}
            percent={value * 100}
            format={percent => `${percent?.toFixed(0)}%`}
            strokeColor={value > 0.8 ? '#52c41a' : value > 0.6 ? '#faad14' : '#ff4d4f'}
          />
        </Space>
      ),
    },
    {
      title: 'Overall Score',
      dataIndex: 'overallScore',
      key: 'overallScore',
      width: 120,
      render: (value: number) => (
        <Statistic
          value={value * 100}
          precision={1}
          suffix="%"
          valueStyle={{
            fontSize: 14,
            color: value > 0.8 ? '#52c41a' : value > 0.6 ? '#faad14' : '#ff4d4f'
          }}
        />
      ),
      sorter: (a: TreatmentStrategy, b: TreatmentStrategy) => a.overallScore - b.overallScore,
      defaultSortOrder: 'descend' as const,
    },
    {
      title: 'Recommendation',
      dataIndex: 'recommendation',
      key: 'recommendation',
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text}>
          <Tag color="blue">{text}</Tag>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="strategy-analysis">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div className="analysis-header">
          <Title level={3} style={{ color: '#437A8B', marginBottom: 8 }}>
            <SettingOutlined style={{ marginRight: 8 }} />
            Treatment Strategy Analysis
          </Title>
          <Text type="secondary">
            AI-powered analysis of ovarian stimulation strategies based on prediction results
          </Text>
        </div>

        {/* Analysis Mode Selection */}
        <Card title="Analysis Configuration" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <Text strong>Analysis Mode:</Text>
              <Radio.Group
                value={analysisMode}
                onChange={(e) => setAnalysisMode(e.target.value)}
                style={{ marginLeft: 16 }}
              >
                <Radio value="full">
                  <Space>
                    <ScanOutlined />
                    Full Scanning (All 83 combinations)
                  </Space>
                </Radio>
                <Radio value="specific">
                  <Space>
                    <ExperimentOutlined />
                    Specific Testing (Custom strategy)
                  </Space>
                </Radio>
              </Radio.Group>
            </div>

            {analysisMode === 'specific' && (
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Text strong>Protocol</Text>
                    <Select
                      placeholder="Select Protocol"
                      value={selectedProtocol}
                      onChange={setSelectedProtocol}
                      style={{ width: '100%' }}
                    >
                      {protocolOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Space>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Text strong>FSH starting dose</Text>
                    <Select
                      placeholder="Select Dose"
                      value={selectedFSHDose}
                      onChange={setSelectedFSHDose}
                      style={{ width: '100%' }}
                    >
                      {fshDoseOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Space>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Text strong>Using rFSH</Text>
                    <Select
                      placeholder="Select Option"
                      value={selectedRFSH}
                      onChange={setSelectedRFSH}
                      style={{ width: '100%' }}
                    >
                      {yesNoOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Space>
                </Col>
                <Col xs={24} sm={12} md={6}>
                  <Space direction="vertical" style={{ width: '100%' }} size="small">
                    <Text strong>LH supplementation</Text>
                    <Select
                      placeholder="Select Option"
                      value={selectedLH}
                      onChange={setSelectedLH}
                      style={{ width: '100%' }}
                    >
                      {yesNoOptions.map(option => (
                        <Option key={option.value} value={option.value}>
                          {option.label}
                        </Option>
                      ))}
                    </Select>
                  </Space>
                </Col>
              </Row>
            )}

            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleAnalyze}
              loading={isAnalyzing}
              disabled={
                analysisMode === 'specific' &&
                (!selectedProtocol || !selectedFSHDose || !selectedRFSH || !selectedLH)
              }
            >
              {isAnalyzing ? 'Analyzing...' : 'Analyze Strategies'}
            </Button>
          </Space>
        </Card>

        {/* Strategy Results */}
        {strategies.length > 0 && (
          <>
            <Card
              title="Recommended Treatment Strategies"
              extra={
                <Space>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadStrategies('por')}
                    disabled={predictionType === 'HOR'}
                  >
                    Download POR
                  </Button>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadStrategies('hor')}
                    disabled={predictionType === 'POR'}
                  >
                    Download HOR
                  </Button>
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadStrategies('combined')}
                    disabled={predictionType !== 'Both'}
                  >
                    Download Combined
                  </Button>
                </Space>
              }
            >
              <Table
                dataSource={strategies}
                columns={columns}
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} strategies`,
                }}
                scroll={{ x: 800 }}
                size="middle"
              />
            </Card>

            <Alert
              message="Strategy Analysis Information"
              description={
                <div>
                  <p><strong>POR Avoidance:</strong> Probability of avoiding poor ovarian response with this strategy.</p>
                  <p><strong>HOR Avoidance:</strong> Probability of avoiding high ovarian response (OHSS risk) with this strategy.</p>
                  <p><strong>Overall Score:</strong> Combined effectiveness score considering both POR and HOR avoidance.</p>
                  <p><strong>Note:</strong> These recommendations are AI-generated based on statistical models and should be validated by clinical experts.</p>
                </div>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
            />
          </>
        )}
      </Space>
    </div>
  );
};

export default StrategyAnalysis;